// Endpoint-list document schema — docs/04-censorship-resistance.md §2.
// This signed document is distributed over independent channels; clients accept
// any copy with a valid Ed25519 signature and a strictly higher `version`. It is
// also the body returned by `GET /endpoints`.
//
// The signed-byte format is fixed by the endpoint tooling (frontend/endpoints):
// Ed25519 over `"merkury-endpoints-v1\n" || canonicalJSON(doc sans signature)`.
// Shape changes here must stay in lockstep with that tool and its fixtures.

import { z } from 'zod';
import { Ed25519Signature } from './primitives.js';

/** SPKI pin for a raw-IP / pinned-cert endpoint, e.g. `sha256/AAAA…`. */
export const SpkiPin = z
  .string()
  .regex(/^sha256\/[A-Za-z0-9+/]+={0,2}$/, 'must be an "sha256/<base64>" SPKI pin');

const WssUrl = z.string().regex(/^wss:\/\/.+/, 'must be a wss:// URL');
const TurnUrl = z
  .string()
  .regex(/^(turns?:|wss:\/\/).+/, 'must be a turn:, turns:, or wss:// URL');

/**
 * A single signaling endpoint (a way to reach *a* signaling server).
 * Raw-IP endpoints have no cert-by-hostname, so they MUST carry an SPKI pin;
 * modelled as a discriminated union so the constraint survives into the
 * exported JSON Schema (a `.refine()` would not).
 */
export const Endpoint = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('domain'), url: WssUrl, spkiPin: SpkiPin.optional() }).strict(),
  z.object({ kind: z.literal('ip'), url: WssUrl, spkiPin: SpkiPin }).strict(),
]);
export type Endpoint = z.infer<typeof Endpoint>;

/** A TURN server entry — same pinning rule, but turn:/turns:/wss: URLs. */
export const TurnEndpoint = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('domain'), url: TurnUrl, spkiPin: SpkiPin.optional() }).strict(),
  z.object({ kind: z.literal('ip'), url: TurnUrl, spkiPin: SpkiPin }).strict(),
]);
export type TurnEndpoint = z.infer<typeof TurnEndpoint>;

const HttpsUrl = z.string().regex(/^https:\/\/.+/, 'must be an https:// URL');

/**
 * A diagnostics intake (docs/10 §5): a write-only report sink, carried here so
 * it rotates through the same signed channel as `turn`/`doh`. It deliberately
 * lives on infrastructure that does NOT share a failure domain with the API —
 * an intake on the blocked host is useless exactly when it is needed.
 *
 * Same pinning rule as everything else: a raw-address intake has no
 * cert-by-hostname, so it MUST carry an SPKI pin.
 */
export const DiagIntake = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('domain'), url: HttpsUrl, spkiPin: SpkiPin.optional() }).strict(),
  z.object({ kind: z.literal('ip'), url: HttpsUrl, spkiPin: SpkiPin }).strict(),
]);
export type DiagIntake = z.infer<typeof DiagIntake>;

/**
 * The full endpoint-list document. `signature` is an Ed25519 signature (key held
 * offline) over the canonical serialization of the document sans `signature`.
 * Versions start at 1; clients accept only strictly greater versions.
 */
export const EndpointList = z
  .object({
    version: z.number().int().min(1),
    signedBy: z.string().min(1),
    signature: Ed25519Signature,
    endpoints: z.array(Endpoint).min(1),
    turn: z.array(TurnEndpoint).optional(),
    doh: z.array(HttpsUrl).optional(),
    /** Diagnostics intakes (docs/10 §5) — inside the signature like everything
     *  else here, so a rotated intake is as trustworthy as a rotated endpoint. */
    diag: z.array(DiagIntake).optional(),
    /** Android self-update metadata (docs/06 §4, 05 §6) — same trust root. */
    apkVersion: z.number().int().min(1).optional(),
    apkSha256: z.string().regex(/^[0-9a-f]{64}$/, 'must be lowercase hex SHA-256').optional(),
    apkMirrors: z.array(z.string().regex(/^https:\/\/.+/, 'must be an https:// URL')).optional(),
  })
  .strict();
export type EndpointList = z.infer<typeof EndpointList>;

/**
 * The unauthenticated reply to `GET /endpoints` (docs/04 §2).
 *
 * Deliberately NOT an `EndpointList`, and the difference is the whole point. A
 * subset of a signed list cannot be served: the signature covers the entire
 * document and this schema is `.strict()`, so any subset fails verification —
 * and a client that adopted one anyway would SHRINK itself, because adoption
 * goes by `version`.
 *
 * So this is a hint, not a list. It carries no version and no signature, and
 * clients must never persist it, never compare it against a stored version, and
 * never let it replace an adopted list. It exists to seed a race for a client
 * that has nothing else to try; the worst a hostile copy can do is cost a
 * connection attempt, which is why it needs no signature to be safe. Anything
 * durable still arrives over the signed channel, authenticated.
 */
export const BootstrapHint = z
  .object({
    /** Discriminator, so a hint can never be mistaken for a list at a glance. */
    hint: z.literal(1),
    endpoints: z.array(z.object({ url: WssUrl }).strict()).min(1).max(8),
  })
  .strict();
export type BootstrapHint = z.infer<typeof BootstrapHint>;
